import type { GlobalTokenSet } from "./registrationApiClient";
import { errorMessage, REGISTRATION_API_URL } from "./registrationApiClient";

// registration-api's webauthn-rs serializes credential IDs/challenges as
// unpadded base64url (Rust's `Base64UrlSafeData`, URL_SAFE_NO_PAD) -- ported
// verbatim from kwms-web's own webauthnClient.ts.
function base64urlToBuffer(base64url: string): ArrayBuffer {
  const padded = base64url.replace(/-/g, "+").replace(/_/g, "/").padEnd(Math.ceil(base64url.length / 4) * 4, "=");
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
}

function bufferToBase64url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

// Server-side shape (webauthn-rs-proto, camelCase, challenge/ids as
// base64url strings) -- converted to the browser's native
// PublicKeyCredentialCreationOptions (ArrayBuffer-based) below. Field names
// match webauthn-rs-proto's attest.rs exactly -- see registration-api's
// webauthn.rs for the Rust side.
interface ServerCreationChallengeResponse {
  publicKey: {
    rp: { id: string; name: string };
    user: { id: string; name: string; displayName: string };
    challenge: string;
    pubKeyCredParams: { type: "public-key"; alg: number }[];
    timeout?: number;
    excludeCredentials?: { type: "public-key"; id: string; transports?: string[] }[];
    authenticatorSelection?: PublicKeyCredentialCreationOptions["authenticatorSelection"];
    attestation?: AttestationConveyancePreference;
    extensions?: Record<string, unknown>;
  };
}

function toCreationOptions(server: ServerCreationChallengeResponse): PublicKeyCredentialCreationOptions {
  const pk = server.publicKey;
  return {
    ...pk,
    challenge: base64urlToBuffer(pk.challenge),
    user: { ...pk.user, id: base64urlToBuffer(pk.user.id) },
    excludeCredentials: pk.excludeCredentials?.map((c) => ({
      ...c,
      id: base64urlToBuffer(c.id),
    })) as PublicKeyCredentialDescriptor[] | undefined,
  };
}

interface ServerRequestChallengeResponse {
  publicKey: {
    challenge: string;
    timeout?: number;
    rpId: string;
    allowCredentials: { type: "public-key"; id: string; transports?: string[] }[];
    userVerification?: UserVerificationRequirement;
    extensions?: Record<string, unknown>;
  };
}

function toRequestOptions(server: ServerRequestChallengeResponse): PublicKeyCredentialRequestOptions {
  const pk = server.publicKey;
  return {
    ...pk,
    challenge: base64urlToBuffer(pk.challenge),
    allowCredentials: pk.allowCredentials.map((c) => ({
      ...c,
      id: base64urlToBuffer(c.id),
    })) as PublicKeyCredentialDescriptor[],
  };
}

// Matches registration-api's RegisterPublicKeyCredential exactly (attest.rs)
// -- "id" is documented there as never actually used server-side (the real
// credential id comes from the attestation data), so any base64url string
// works; using credential.id (already base64url per spec) is simplest.
function registrationCredentialToJson(credential: PublicKeyCredential): unknown {
  const response = credential.response as AuthenticatorAttestationResponse;
  return {
    id: credential.id,
    rawId: bufferToBase64url(credential.rawId),
    type: credential.type,
    response: {
      attestationObject: bufferToBase64url(response.attestationObject),
      clientDataJSON: bufferToBase64url(response.clientDataJSON),
    },
  };
}

// Matches registration-api's PublicKeyCredential exactly (auth.rs).
function authenticationCredentialToJson(credential: PublicKeyCredential): unknown {
  const response = credential.response as AuthenticatorAssertionResponse;
  return {
    id: credential.id,
    rawId: bufferToBase64url(credential.rawId),
    type: credential.type,
    response: {
      authenticatorData: bufferToBase64url(response.authenticatorData),
      clientDataJSON: bufferToBase64url(response.clientDataJSON),
      signature: bufferToBase64url(response.signature),
      userHandle: response.userHandle ? bufferToBase64url(response.userHandle) : null,
    },
  };
}

/**
 * Enrolls a passkey for the already-logged-in caller (`accessToken` from
 * the QR web-login that must precede this -- see registration-api's
 * webauthn.rs: registration is bearer-auth, "attach a passkey to my
 * already-open session", not a standalone identity proof. The registered
 * credential works for logging into kwms-web too (and vice versa) --
 * registration-api's WEBAUTHN_RP_ID is the bare apex `uskorin.tech`, not a
 * per-app subdomain, see project_kwms_android_passkey_rpid_fix.
 */
export async function registerPasskey(accessToken: string, phone: string): Promise<void> {
  const startResponse = await fetch(`${REGISTRATION_API_URL}/webauthn/register/start`, {
    method: "POST",
    // registration-api runs 2+ replicas behind a sticky-session cookie
    // (registration-api-affinity) -- without credentials: "include" here,
    // register/start and register/finish can land on different replicas,
    // and finish's in-memory pending-ceremony lookup fails.
    credentials: "include",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${accessToken}` },
    body: JSON.stringify({ phone }),
  });
  if (!startResponse.ok) {
    throw new Error(await errorMessage(startResponse, "Не удалось начать регистрацию ключа"));
  }
  const challenge: ServerCreationChallengeResponse = await startResponse.json();

  const credential = await navigator.credentials.create({ publicKey: toCreationOptions(challenge) });
  if (!credential) throw new Error("Не удалось создать ключ доступа");

  const finishResponse = await fetch(`${REGISTRATION_API_URL}/webauthn/register/finish`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${accessToken}` },
    body: JSON.stringify(registrationCredentialToJson(credential as PublicKeyCredential)),
  });
  if (!finishResponse.ok) {
    throw new Error(await errorMessage(finishResponse, "Не удалось сохранить ключ доступа"));
  }
}

/**
 * Logs in a returning owner-staff who already enrolled a passkey (see
 * OfferPasskeyStep.tsx) -- `phone` here is only a lookup key (see
 * registration-api's `/webauthn/login/start` doc comment), the WebAuthn
 * signature itself is what proves identity. Returns the same global-token
 * shape the QR flow does, so the caller can feed it straight into
 * authApiLocalClient.ts's own `globalLogin`.
 */
export async function loginWithPasskey(phone: string): Promise<GlobalTokenSet> {
  const startResponse = await fetch(`${REGISTRATION_API_URL}/webauthn/login/start`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ phone }),
  });
  if (!startResponse.ok) {
    // registration-api returns this exact (untranslated) message when the
    // phone has no passkey registered at all -- the one case worth telling
    // apart from a generic failure, since the fix is simply "use the QR".
    const message = await errorMessage(startResponse, "Не удалось начать вход по ключу доступа");
    throw new Error(
      message.includes("no passkey registered")
        ? "Ключ доступа не найден для этого номера — войдите по QR"
        : message,
    );
  }
  const { authentication_id: authenticationId, challenge }: { authentication_id: string; challenge: ServerRequestChallengeResponse } =
    await startResponse.json();

  const credential = await navigator.credentials.get({ publicKey: toRequestOptions(challenge) });
  if (!credential) throw new Error("Не удалось подтвердить ключ доступа");

  const finishResponse = await fetch(`${REGISTRATION_API_URL}/webauthn/login/finish`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      authentication_id: authenticationId,
      credential: authenticationCredentialToJson(credential as PublicKeyCredential),
    }),
  });
  if (!finishResponse.ok) {
    throw new Error(await errorMessage(finishResponse, "Не удалось войти по ключу доступа"));
  }
  const body: {
    phone: string;
    user_uid: string;
    access_token: string;
    id_token?: string;
    refresh_token?: string;
    expires_in: number;
    token_type: string;
  } = await finishResponse.json();

  return {
    accessToken: body.access_token,
    idToken: body.id_token,
    refreshToken: body.refresh_token,
    tokenType: body.token_type,
    expiresAtEpochSeconds: Math.floor(Date.now() / 1000) + body.expires_in,
    userUid: body.user_uid,
    phone: body.phone,
  };
}
